import { hasTrussDesignPermission } from '@paragon/login-tools';
import { enqueueError } from '@paragon/notification-tools';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { getSingleSelectedItemIdentifierOrNull } from '../../library/selection/selectors';
import { isConsideredDesignItem, isConsideredLayoutItem, SelectedItemIdentifier } from '../../library/selection/types';
import { focusCameraOnSelectedObjects } from '../../three/ThreeJsSceneActions';
import { getBlockingOperation } from '../selectors/blockingOperationSelectors';
import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import { getProjectLayoutModel } from '../selectors/getProjectLayoutModel';
import { getSelectedEntryFactory } from '../selectors/getSelectedItemSelector';
import { getSortedComponentDesigns } from '../selectors/getSortedComponentDesignsSelectors';
import { isLayoutModeEnabled } from '../selectors/layoutModeSelectors';
import { inViewOnlyMode } from '../selectors/viewOnlyModeSelectors';
import { setDimensionMode } from '../slices/applicationSlice';
import { cameraSlice } from '../slices/camera/cameraSlice';
import { getCurrentCameraType } from '../slices/camera/selectors';
import { CameraType, getRenderModeForCameraType, RenderMode } from '../slices/camera/types';
import { startInsertComponentDesign } from './insertComponentDesignActions';
import { resetSelectedItems, selectEntry, SelectionOptions } from './selectActions';
import { ThunkActionVoid } from './thunkActions';

export const setActiveCamera =
  (type: CameraType): ThunkActionVoid =>
  (dispatch, getState) => {
    const renderMode = getRenderModeForCameraType(type);

    if (renderMode === 'layout' && !isLayoutModeEnabled(getState())) return;

    dispatch(cameraSlice.actions.activeCameraChanged(type));
  };

export const toggleLayout3DCamera = (): ThunkActionVoid => (dispatch, getState) => {
  const camera = getCurrentCameraType(getState());
  switch (camera) {
    case 'orthographic-design':
      return;
    case 'perspective-layout':
      dispatch(setActiveCamera('orthographic-layout'));
      return;
    case 'orthographic-layout':
      dispatch(setActiveCamera('perspective-layout'));
      return;
    default:
      assertExhaustiveSwitch(camera);
  }
};

interface SetRenderModeOptions {
  /**
   * Whether to force the change in render mode even if a blocking operation is ongoing.
   * @default false
   */
  readonly force?: boolean;
}

export const setRenderModeAndEnsureSelectedItemIsAppropriate =
  (desiredRenderMode: RenderMode, { force }: SetRenderModeOptions = {}): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();

    if (!isLayoutModeEnabled(state)) return;

    if (!force) {
      const blockingOperation = getBlockingOperation(state);
      if (blockingOperation) {
        dispatch(enqueueError(`Cannot switch modes while ${blockingOperation.userVisibleDescription}.`));
        return;
      }
    }

    const selectedItem = getSingleSelectedItemIdentifierOrNull(state);

    dispatch(setDimensionMode(false));

    let itemsToSelect: SelectedItemIdentifier[] = [];
    const selectionOptions: SelectionOptions = { intent: 'select', exclusive: true };

    // Items might have been deleted since they were last selected. Due to the diversity of deletion actions, we
    //   don't attempt to keep `mostRecentlySelected...` pruned over time.
    function prunePreviouslySelectedItems(items: SelectedItemIdentifier[]) {
      const entryFactory = getSelectedEntryFactory(getState());
      return items.filter((item) => entryFactory(item) != null);
    }

    switch (desiredRenderMode) {
      case 'design': {
        if (selectedItem && isConsideredDesignItem(selectedItem.type)) return;

        const previouslySelectedItems = prunePreviouslySelectedItems(
          state.selections.mostRecentlySelectedDesignModeItems,
        );
        if (previouslySelectedItems.length > 0) {
          itemsToSelect = previouslySelectedItems;
          selectionOptions.retainCameraPosition = true;
          break;
        }

        const activeComponentDesign = getActiveComponentDesign(state);
        if (activeComponentDesign) {
          itemsToSelect = [{ type: 'componentDesign', guid: activeComponentDesign.guid }];
          selectionOptions.retainCameraPosition = true;
          break;
        }

        const allComponentDesigns = getSortedComponentDesigns(state);
        if (allComponentDesigns.length > 0) {
          itemsToSelect = [{ type: 'componentDesign', guid: allComponentDesigns[0].guid }];
          break;
        }

        break;
      }

      case 'layout': {
        if (selectedItem && isConsideredLayoutItem(selectedItem.type)) return;

        const previouslySelectedItems = prunePreviouslySelectedItems(
          state.selections.mostRecentlySelectedLayoutModeItems,
        );

        if (previouslySelectedItems.length > 0) {
          itemsToSelect = previouslySelectedItems;

          selectionOptions.retainCameraPosition = true;
          selectionOptions.retainLayoutHierarchyFilters = true;

          break;
        }

        const project = getProjectLayoutModel(state);
        if (project) {
          itemsToSelect = [{ type: 'project', guid: project.guid }];

          selectionOptions.retainCameraPosition = true;
          selectionOptions.retainLayoutHierarchyFilters = true;

          break;
        }

        break;
      }

      default:
        assertExhaustiveSwitch(desiredRenderMode);
    }

    if (itemsToSelect.length > 0) {
      const lookUpEntry = getSelectedEntryFactory(state);

      for (const item of itemsToSelect) {
        const entry = lookUpEntry(item);

        if (entry) {
          dispatch(selectEntry(entry, selectionOptions));
        }
      }
    } else if (desiredRenderMode === 'design' && hasTrussDesignPermission(state) && !inViewOnlyMode(state)) {
      dispatch(startInsertComponentDesign());
    }
  };

export const resetView = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();
  if (state.editor.editInProgress) return;

  const itemsDeselected = dispatch(resetSelectedItems());

  // This is a two-stage action. The first time it's called, we want to deselect the selected item(s). The second time
  //   it's called (or the first time if no items were in need of deselection), we want to finish resetting the view.
  if (itemsDeselected) return;

  focusCameraOnSelectedObjects();
  dispatch(setDimensionMode(false));
};
